import { Surface } from '@cloudflare/kumo/components/surface';

interface Year2025Detail {
  exhibitorId: string;
  needsTable: boolean;
  needsChair: boolean;
  additionalTables: number;
  additionalChairs: number;
  needsPower: boolean;
  powerCapacity: string | null;
  customPowerCapacity: number | null;
  equipmentFee: number;
  powerFee: number;
}

interface Props {
  exhibitor: {
    type: string;
    venueFeeTier: string | null;
    boothSizePreference: string;
    isHalfBooth: boolean;
    totalFee: number;
    finalVenueFee?: number | null;
    finalEquipmentFee?: number | null;
    finalPowerFee?: number | null;
    finalTotalFee?: number | null;
    isFree: boolean;
  };
  yearDetail: Year2025Detail | null;
}

const POWER_LABEL: Record<string, string> = {
  under100VA: '100VA 以下',
  under200VA: '200VA 以下',
  over200VA: '200VA 超 (カスタム)',
};

export default function ExhibitorView2025({ exhibitor, yearDetail }: Props) {
  return (
    <Surface className="p-6">
      <div className="mb-3 flex items-center justify-between">
        <h2 className="text-base font-semibold text-kumo-default">2025 年度 申込内容 (アーカイブ表示)</h2>
        <span className="text-xs text-kumo-muted">read-only</span>
      </div>

      <div className="grid grid-cols-2 gap-3 text-sm">
        <Row label="出展種別" value={exhibitor.type} />
        <Row label="会場費ティア" value={exhibitor.venueFeeTier ?? '—'} />
        <Row label="ブースサイズ希望" value={exhibitor.boothSizePreference} />
        <Row label="0.5 ブース設定" value={exhibitor.isHalfBooth ? '有' : '無'} />
      </div>

      <div className="my-4 h-px bg-kumo-line" />

      <h3 className="mb-2 text-sm font-semibold text-kumo-default">機材・電源 (2025 形式)</h3>
      {yearDetail ? (
        <div className="grid grid-cols-2 gap-3 text-sm">
          <Row label="机椅子セット" value={yearDetail.needsTable || yearDetail.needsChair ? '有' : '無'} />
          <Row label="追加机" value={String(yearDetail.additionalTables)} />
          <Row label="追加椅子" value={String(yearDetail.additionalChairs)} />
          <Row label="電源" value={yearDetail.needsPower ? '有' : '無'} />
          <Row
            label="電源容量"
            value={
              yearDetail.powerCapacity
                ? `${POWER_LABEL[yearDetail.powerCapacity] ?? yearDetail.powerCapacity}` +
                  (yearDetail.customPowerCapacity ? ` (${yearDetail.customPowerCapacity}VA)` : '')
                : '—'
            }
          />
          <Row label="備品費" value={`¥${yearDetail.equipmentFee.toLocaleString()}`} />
          <Row label="電源費" value={`¥${yearDetail.powerFee.toLocaleString()}`} />
        </div>
      ) : (
        <p className="text-sm text-kumo-muted">2025 年度詳細データがありません</p>
      )}

      <div className="my-4 h-px bg-kumo-line" />

      <h3 className="mb-2 text-sm font-semibold text-kumo-default">料金</h3>
      <div className="grid grid-cols-2 gap-3 text-sm">
        <Row label="合計金額" value={`¥${exhibitor.totalFee.toLocaleString()}`} />
        <Row label="全額無料" value={exhibitor.isFree ? '有' : '無'} />
        <Row label="最終会場費 (override)" value={fmt(exhibitor.finalVenueFee)} />
        <Row label="最終備品費 (override)" value={fmt(exhibitor.finalEquipmentFee)} />
        <Row label="最終電源費 (override)" value={fmt(exhibitor.finalPowerFee)} />
        <Row label="最終合計 (override)" value={fmt(exhibitor.finalTotalFee)} />
      </div>
    </Surface>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-xs text-kumo-muted">{label}</span>
      <span className="text-sm text-kumo-default">{value}</span>
    </div>
  );
}

function fmt(v: number | null | undefined): string {
  return v === null || v === undefined ? '—' : `¥${v.toLocaleString()}`;
}
